import { type QueryParams } from './query-params/types';

/**
 * Extracts the base URL from a given URL if it exists. Otherwise, it returns the current origin.
 * @param url string or undefined
 * @returns base URL or current origin
 */
export function getBaseUrl(url?: string): string {
	return url ? new URL(url).origin : window.location.origin;
}

/**
 * Generates a search URL with the given query parameters.
 * @param queryParams Record<string, string>
 * @returns search URL
 */
export function defaultGenerateSearchUrl(queryParams: QueryParams): string {
	const url = new URL(window.location.href);
	const searchParams = new URLSearchParams();
	Object.entries(queryParams).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			searchParams.set(key, String(value));
		} else {
			searchParams.delete(key);
		}
	});
	url.search = searchParams.toString();
	return url.href;
}

export const defaultOnNavigate = {
	callback: (url: string): void => {
		window.location.href = url;
	},
	options: {
		alwaysUseOnLinkClick: false,
	},
};

export const isOnJiraFullPageSearch = (): boolean => {
	return window.location.pathname.includes('/jira/rovo-search');
};

/**
 * Checks if the user is currently on the search page route by comparing
 * the current pathname with the pathname generated by `generateSearchUrl`.
 *
 * Returns `undefined` if URL parsing fails.
 *
 * @param generateSearchUrl - A function that generates the search URL for the given query parameters.
 *                            Defaults to `defaultGenerateSearchUrl`.
 * @returns `true` if the current pathname matches the search page pathname.
 */
export function isOnSearchRoute(
	generateSearchUrl: (queryParams: QueryParams) => string = defaultGenerateSearchUrl,
): boolean | undefined {
	try {
		const searchPath = new URL(generateSearchUrl({})).pathname;
		const currentPath = new URL(window.location.href).pathname;

		return searchPath === currentPath;
	} catch {
		return undefined;
	}
}

/* TODO: Remove this when the backend stops sending dummy board URLs,
         as locationless boards will be handled by the backend and this check will no longer be needed.
 */
export const isDummyBoardUrl = (url: string | undefined): boolean =>
	Boolean(url?.endsWith('/browse/DummyJiraBoard'));
